// ./src/utils/api/ReminderService.ts
//
// Reminder (scheduled per-event reminders — roadmap #13) service. OpResult-native. On the event
// Reminders page every list is filtered by eventId. A reminder carries a kind (event-approaching /
// rsvp-nudge), a delivery channel, a schedule (before-event offset OR an absolute time), an optional
// recurrence (every N days), an audience, and a message. Full single-item CRUD plus a bulk status
// change / remove (array of ids in the where clause), and a manual "send now" (with a dryRun preview).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const REMINDER_FIELDS = `
  id
  eventId
  accountId
  name
  kind
  channel
  scheduleKind
  offsetMinutes
  repeatEveryDays
  sendAt
  audience { rsvpStatus roles groupIds }
  messageSubject
  messageBody
  templateId
  lastRunAt
  status
  rights
  createdAt
  updatedAt
`;

const REMINDER_LIST_QUERY = gql`
  query ReminderList($filter: ReminderFilter, $params: PaginationAndSorting) {
    reminderList(filter: $filter, params: $params) {
      code
      data { ${REMINDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_GET_QUERY = gql`
  query ReminderGet($id: ID) {
    reminderGet(id: $id) {
      code
      data { ${REMINDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_GET_MANY_QUERY = gql`
  query ReminderGetMany($ids: [ID]) {
    reminderGetMany(ids: $ids) {
      code
      data { ${REMINDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_CREATE_MUTATION = gql`
  mutation ReminderCreate($params: ReminderInput) {
    reminderCreate(params: $params) {
      code
      data { ${REMINDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_UPDATE_MUTATION = gql`
  mutation ReminderUpdate($where: ReminderWhereInput, $params: ReminderInput) {
    reminderUpdate(where: $where, params: $params) {
      code
      data { ${REMINDER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_REMOVE_MUTATION = gql`
  mutation ReminderRemove($where: ReminderWhereInput) {
    reminderRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_SEND_NOW_MUTATION = gql`
  mutation ReminderSendNow($id: ID, $dryRun: Boolean) {
    reminderSendNow(id: $id, dryRun: $dryRun) {
      code
      data {
        reminderId
        eventId
        occurrenceKey
        dryRun
        skipped
        recipientsTotal
        withContact
        withoutContact
        sent
        failed
        nextSendAt
      }
      errors { name errors warnings }
    }
  }
`;

const REMINDER_CATALOG_QUERY = gql`
  query ReminderCatalog($eventId: ID!, $kind: String!, $channel: String) {
    reminderCatalog(eventId: $eventId, kind: $kind, channel: $channel) {
      code
      errors { name errors warnings }
      data {
        recommendedId
        eventCategoryCode
        messages {
          id
          label
          eventType
          tone
          subject
          body
        }
      }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Reminder schema)
// =============================================================================

/** What triggers a reminder. */
export const REMINDER_KIND = {
  EVENT_APPROACHING: "event_approaching",
  RSVP_NO_RESPONSE: "rsvp_no_response",
} as const;

export type ReminderKind = (typeof REMINDER_KIND)[keyof typeof REMINDER_KIND];

/** Delivery channel. (WhatsApp exists on the server but is not offered in the UI yet.) */
export const REMINDER_CHANNEL = {
  EMAIL: "email",
  SMS: "sms",
} as const;

export type ReminderChannel = (typeof REMINDER_CHANNEL)[keyof typeof REMINDER_CHANNEL];

/** How the fire time is set. */
export const REMINDER_SCHEDULE_KIND = {
  BEFORE_EVENT: "before_event",
  ABSOLUTE: "absolute",
} as const;

export type ReminderScheduleKind = (typeof REMINDER_SCHEDULE_KIND)[keyof typeof REMINDER_SCHEDULE_KIND];

export interface ReminderAudience {
  rsvpStatus?: string[] | null;
  roles?: string[] | null;
  groupIds?: string[] | null;
}

export interface Reminder {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  name?: string | null;
  kind?: string | null;
  channel?: string | null;
  scheduleKind?: string | null;
  offsetMinutes?: number | null;
  repeatEveryDays?: number | null;
  sendAt?: string | null;
  audience?: ReminderAudience | null;
  messageSubject?: string | null;
  messageBody?: string | null;
  templateId?: string | null;
  lastRunAt?: string | null;
  status?: number | null;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReminderInput {
  eventId?: string | null;
  name?: string | null;
  kind?: string | null;
  channel?: string | null;
  scheduleKind?: string | null;
  offsetMinutes?: number | null;
  repeatEveryDays?: number | null;
  sendAt?: string | null;
  audience?: ReminderAudience | null;
  messageSubject?: string | null;
  messageBody?: string | null;
  templateId?: string | null;
  status?: number | null;
}

export interface ReminderFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  kind?: string[] | null;
  channel?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface ReminderWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

/** The breakdown returned by reminderSendNow (a live fire OR a dryRun preview). */
export interface ReminderSendReport {
  reminderId?: string | null;
  eventId?: string | null;
  occurrenceKey?: string | null;
  dryRun?: boolean | null;
  skipped?: boolean | null;
  recipientsTotal?: number | null;
  withContact?: number | null;
  withoutContact?: number | null;
  sent?: number | null;
  failed?: number | null;
  nextSendAt?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class ReminderService {
  static async list(filter?: ReminderFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(REMINDER_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(REMINDER_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(REMINDER_GET_MANY_QUERY, { ids });
  }

  static async create(params: ReminderInput): Promise<OpResult> {
    return ApiService.executeMutation(REMINDER_CREATE_MUTATION, { params });
  }

  static async update(where: ReminderWhereInput, params: ReminderInput): Promise<OpResult> {
    return ApiService.executeMutation(REMINDER_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMINDER_REMOVE_MUTATION, { where: { id } });
  }

  /** Remove every selected reminder in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(REMINDER_REMOVE_MUTATION, { where: { id: ids } });
  }

  /** Fire a reminder now (owner-triggered). dryRun resolves the audience and reports the breakdown without sending. */
  static async sendNow(id: string, dryRun = false): Promise<OpResult> {
    return ApiService.executeMutation(REMINDER_SEND_NOW_MUTATION, { id, dryRun });
  }

  /**
   * The message-preset catalog for a reminder kind + channel — tone variants (Friendly/Funny/Formal/…) the
   * host picks from, with a recommended pick for the event's occasion. Mirrors the invitation/update catalog.
   */
  static async reminderCatalog(eventId: string, kind: string, channel?: string): Promise<OpResult> {
    return ApiService.executeQuery(REMINDER_CATALOG_QUERY, { eventId, kind, channel }, { fetchPolicy: "network-only" });
  }
}

/** One reminder message preset from the catalog (a subject + body with tokens the host can pick + edit). */
export interface ReminderCatalogMessage {
  id?: string;
  label?: string | null;
  eventType?: string | null;
  /** warm | formal | playful | simple. */
  tone?: string | null;
  subject?: string | null;
  body?: string | null;
}

export interface ReminderCatalog {
  recommendedId?: string | null;
  eventCategoryCode?: string | null;
  messages?: ReminderCatalogMessage[] | null;
}
